// modules
import { useCallback, useEffect, useMemo, useState } from "react";
import { To, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";

// components
import CountDownModal from "components/molecules/ModalContainer/ModalContainer";

// hooks
import { useAppSelector } from "app/hooks/hooks";

// redux
import { getLoadingState, getLoggedInUserInfo, hasUnaurizedErrorSelector, getInactiveTimeSelector } from "redux/userList/userSlice";
import { hasTokenErrRetroSelector } from "redux/retroClaim/retroClaimSlice";
import { hasTokenErrRoleMgmtSelector } from "redux/roleManagement/roleManagementSlice";
import { hasTokenErrReimbSelector } from "redux/reimbursement/reimbursementSlice";

// types
import { Users } from "redux/types";

// constants
import { SideNavConstants as Constants } from "constants/sidenavigation";

// utils
import { generateSessionMLink } from "utils/generateSessionMLink";
import { LOGOUT_TIMEOUT, Routes } from "constants/global";
import { invalidateCookie } from "utils/token";

// auth
import { Auth } from "app/hooks/Auth";

export type NavigationMenuItem = {
	id: number | number[];
	icon: string;
	label: string;
	route: To | To[];
	code?: string | string[];
	showDivider?: boolean;
	protected?: boolean;
};

/**
 * Checks if a menu item has matching permissions
 */
function hasMatchingPermissions(menuCode: string | string[] | undefined, permissions: string[]): number[] {
	if (!menuCode) return [];

	const codes = Array.isArray(menuCode) ? menuCode : [menuCode];
	const matchingIndices: number[] = [];

	codes.forEach((code, index) => {
		if (permissions?.includes(code)) {
			matchingIndices.push(index);
		}
	});

	return matchingIndices;
}

/**
 * Creates a filtered menu item based on matching permission indices
 */
function createFilteredMenuItem(menu: NavigationMenuItem, matchingIndices: number[]): NavigationMenuItem {
	return {
		...menu,
		code: Array.isArray(menu.code) ? matchingIndices.map((i) => (menu.code as string[])[i]) : menu.code,
		route: Array.isArray(menu.route) ? matchingIndices.map((i) => (menu.route as To[])[i]) : menu.route,
		id: Array.isArray(menu.id) ? matchingIndices.map((i) => (menu.id as number[])[i]) : menu.id
	};
}

/**
 * Handles divider menu items
 */
function shouldAddDivider(menu: NavigationMenuItem, dividerIndex: number, menuIndex: number): boolean {
	return menu?.code === "divider" && (dividerIndex === -1 || dividerIndex + 1 !== menuIndex + 1);
}

/**
 * Checks if user has Offer Issuance permissions
 */
const hasOfferIssuanceAccess = (permissions: string[]): { hasApprove: boolean; hasCreate: boolean } => {
	return {
		hasApprove: permissions.includes("OFFER_ISSUANCE_APPROVE"),
		hasCreate: permissions.includes("OFFER_ISSUANCE_CREATE")
	};
};

/**
 * Determines the correct route for Offer Issuance based on permissions
 */
const getOfferIssuanceRoute = (permissions: string[]): To => {
	const { hasApprove, hasCreate } = hasOfferIssuanceAccess(permissions);
	// If user has either permission, they should be directed to the approval page
	if (hasApprove || hasCreate) {
		return Routes.OFFER_ISSUANCE_APPROVE;
	}
	return Routes.OFFER_ISSUANCE;
};

/**
 * Filters menu items based on user permissions
 */
function filterMenuItems(_permissions: string[], _navigationMenuItems: NavigationMenuItem[]) {
	const menuItems: NavigationMenuItem[] = [];
	let dividerIndex = -1;
	let menuIndex = -1;

	_navigationMenuItems?.forEach((menu) => {
		// Case 1: Protected menu that requires permission check
		if (menu?.code && menu?.protected) {
			const matchingIndices = hasMatchingPermissions(menu.code, _permissions);

			if (matchingIndices.length > 0) {
				// Special handling for Offer Issuance routes
				if (menu.label === "Offer Issuance") {
					const { hasApprove, hasCreate } = hasOfferIssuanceAccess(_permissions);
					if (!hasApprove && !hasCreate) {
						return; // Don't show the menu if user has no access
					}
					// Override the route based on permissions
					menu.route = getOfferIssuanceRoute(_permissions);
				}

				const filteredMenu = createFilteredMenuItem(menu, matchingIndices);
				menuItems.push(filteredMenu);
				menuIndex++;
			}
		}
		// Case 2: Unprotected menu items
		else if (!menu?.protected) {
			if (shouldAddDivider(menu, dividerIndex, menuIndex)) {
				menuItems.push(menu);
				dividerIndex = menuIndex + 1;
				menuIndex++;
			} else if (menu?.code !== "divider") {
				menuItems.push(menu);
				menuIndex++;
			}
		}
	});

	return menuItems;
}

/**
 * Extracts permissions from user groups
 */
function extractPermissionsFromGroups(groups: any[] = []): string[] {
	const permissions: string[] = [];

	groups.forEach((group: { permissions: Array<{ perm_code: string }> }) => {
		const groupPermissions = group?.permissions || [];
		groupPermissions.forEach((permission) => {
			permissions.push(permission?.perm_code);
		});
	});

	return permissions;
}

/**
 * Handles user session timeout
 */
function handleSessionTimeout(navigate: ReturnType<typeof useNavigate>) {
	const userPool = new Auth().getUserPool();
	const currentUser = userPool?.getCurrentUser();

	if (currentUser) {
		currentUser.signOut();
	}
	invalidateCookie();

	CountDownModal({
		type: "confirm",
		title: Constants.LOGOUT_TITLE,
		subtitle: Constants.LOGOUT_SUBTITLE,
		cancelButton: {
			label: Constants.LOGOUT_OKAY_BTN,
			onClick: () => {
				navigate(Routes.LOGOUT);
			},
			classes: {
				label: "back_button_label_white",
				buttonInput: "back_button_violet"
			}
		}
	});
}

/**
 * Custom hook to handle user data and permissions
 */
function useUserData() {
	const userInfo: Users["user"] = useAppSelector(getLoggedInUserInfo);

	// Extract user data
	const userData = useMemo(() => {
		return userInfo?.data?.[0] || {};
	}, [userInfo]);

	const { tenant_code, tenant_name, outlets, first_name, last_name, username, groups } = userData;

	// Determine if user is admin
	const isAdminUser = !tenant_code && !tenant_name;

	// Get appropriate navigation menu based on user type
	const navigationMenuItems: NavigationMenuItem[] = isAdminUser ? Constants.ADMIN_MENU : Constants.TENANT_MENU;

	// Prepare user display information
	const userName = isAdminUser ? `${first_name} ${last_name}` : tenant_name;
	const userDetails = useMemo(() => {
		return {
			name: userName,
			code: !isAdminUser ? `Tenant Code: ${tenant_code}` : `Admin ID: ${username}`
		};
	}, [userName, tenant_code, username, isAdminUser]);

	// Extract permissions from user groups
	const permissions = useMemo(() => {
		return extractPermissionsFromGroups(groups);
	}, [groups]);

	return {
		isAdminUser,
		outlets,
		tenant_code,
		userDetails,
		permissions,
		navigationMenuItems
	};
}

/**
 * Custom hook to handle menu state and navigation
 */
function useMenuNavigation(navigationMenuItems: NavigationMenuItem[], permissions: string[]) {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [activeMenu, setActiveMenu] = useState<number>(0);
	const [open, setOpen] = useState<boolean>(false);

	// Filter menu items based on user permissions
	const menusToRender = useMemo(() => {
		return filterMenuItems(permissions, navigationMenuItems);
	}, [permissions, navigationMenuItems]);

	// Find currently selected menu item
	const currentSelectedItem: NavigationMenuItem | undefined = useMemo(() => {
		return navigationMenuItems?.find((menuItem: NavigationMenuItem): boolean => {
			if (Array.isArray(menuItem.id)) {
				return menuItem.id.includes(activeMenu);
			}
			const menuRoute = Array.isArray(menuItem.route) ? menuItem.route[0] : menuItem.route;
			return menuRoute === pathname;
		});
	}, [navigationMenuItems, activeMenu, pathname]);

	// Update active menu when current selection changes
	useEffect(() => {
		if (currentSelectedItem?.id) {
			setActiveMenu(Array.isArray(currentSelectedItem.id) ? currentSelectedItem.id[0] : currentSelectedItem.id);
		}
	}, [currentSelectedItem]);

	// Toggle sidebar
	const handleToggle = useCallback(() => {
		setOpen(!open);
	}, [open]);

	// Helper function to determine the correct navigation path
	const getNavigationPath = useCallback(
		(menuId: number, redirectedPath: To | To[]): To => {
			if (!Array.isArray(redirectedPath)) {
				return redirectedPath;
			}

			const menu = navigationMenuItems.find((item) => (Array.isArray(item.id) ? item.id.includes(menuId) : item.id === menuId));

			if (menu && Array.isArray(menu.id)) {
				const index = menu.id.indexOf(menuId);
				return redirectedPath[index];
			}

			return redirectedPath[0];
		},
		[navigationMenuItems]
	);

	// Handle SessionM specific logic
	const handleSessionMClick = useCallback(async () => {
		await generateSessionMLink();
	}, []);

	// Handle menu item click
	const handleMenuClick = useCallback(
		async (menuId: number, redirectedPath: To | To[]) => {
			// Set active menu
			setActiveMenu(menuId);

			// Navigate to the appropriate path
			const path = getNavigationPath(menuId, redirectedPath);

			if (String(path).startsWith("/v2/")) {
				const v2Path = window.location.origin + path;
				// const v2Path = "http://localhost:3001" + path;
				location.href = v2Path;
				return;
			}

			navigate(path);

			// Toggle menu state
			handleToggle();

			// Handle special case for SessionM menu
			if (menuId === 25) {
				await handleSessionMClick();
			}
		},
		[navigate, handleToggle, getNavigationPath, handleSessionMClick]
	);

	return {
		open,
		activeMenu,
		menusToRender,
		handleToggle,
		handleMenuClick
	};
}

/**
 * Custom hook to handle session timeout
 */
function useSessionTimeout() {
	const navigate = useNavigate();
	const inactiveTime = useAppSelector(getInactiveTimeSelector);

	// Set up idle timer for session timeout
	useIdleTimer({
		timeout: inactiveTime * 60 * 1000 || LOGOUT_TIMEOUT,
		stopOnIdle: true,
		onIdle: () => handleSessionTimeout(navigate)
	});
}

/**
 * Custom hook to handle authentication errors
 */
function useAuthErrors() {
	const navigate = useNavigate();
	const hasUnaurizedError = useAppSelector(hasUnaurizedErrorSelector);
	const isAuthErrInRetro = useAppSelector(hasTokenErrRetroSelector);
	const isAuthErrInRoleMgmt = useAppSelector(hasTokenErrRoleMgmtSelector);
	const isAuthErrInReimb = useAppSelector(hasTokenErrReimbSelector);

	// Handle unauthorized errors
	useEffect(() => {
		const hasAuthError = hasUnaurizedError || isAuthErrInRetro || isAuthErrInRoleMgmt || isAuthErrInReimb;
		if (hasAuthError) {
			navigate(Routes.LOGOUT);
		}
	}, [hasUnaurizedError, isAuthErrInRetro, isAuthErrInRoleMgmt, isAuthErrInReimb, navigate]);
}

/**
 * @description: Hook to restrict user from accessing not authorised pages.
 */
export function useHome() {
	const isHomePage = useMatch("/");
	const dataLoading = useAppSelector(getLoadingState) as unknown as number;

	// Use extracted hooks
	const { isAdminUser, outlets, tenant_code, userDetails, permissions, navigationMenuItems } = useUserData();
	const { open, activeMenu, menusToRender, handleToggle, handleMenuClick } = useMenuNavigation(navigationMenuItems, permissions);

	// Check if user has USER_PROFILE_VIEW permission
	// No need for useMemo here as permissions is already memoized and this is a simple boolean check
	const hasUserProfileViewPermission = permissions?.includes("USER_PROFILE_VIEW") ?? false;

	// Handle username click to navigate to user profile
	const handleUsernameClick = useCallback(() => {
		if (hasUserProfileViewPermission) {
			const v2Path = window.location.origin + "/v2/user-profile/edit";
			location.href = v2Path;
		}
	}, [hasUserProfileViewPermission]);

	// Set up session timeout
	useSessionTimeout();

	// Handle auth errors
	useAuthErrors();

	return {
		isAdminUser,
		loading: dataLoading === 1,
		outlets,
		open,
		tenant_code,
		isHomePage,
		userDetails,
		menusToRender,
		activeMenu,
		handleToggle,
		handleMenuClick,
		permissions,
		handleUsernameClick,
		hasUserProfileViewPermission
	};
}
